<template>
  <div class="external-content-view">
    <SelectFolderModalToolbar
      v-if="showSearch"
      :value="searchKey"
      :band="bandToolbar"
      :placeholder="bandToolbar ? 'Search…' : 'Search...'"
      @input="onSearchInput"
      @search="onSearchSubmit"
      @clear="clearSearch"
    >
      <template #breadcrumb>
        <slot name="toolbar-start" />
      </template>
    </SelectFolderModalToolbar>

    <div
      v-if="isLoading"
      class="external-content-view__state"
    >
      <b-spinner />
    </div>

    <div
      v-else-if="items.length === 0"
      class="external-content-view__state"
    >
      No items found.
    </div>

    <div
      v-else-if="fitRows"
      class="external-content-view__sections"
    >
      <ItemGrid
        :items="items"
        :is-selected="isSelected"
        :columns="gridColumns"
        :columns-md="gridColumnsMd"
        :item-props="{ isAvailableOverlay: false }"
        :item-props-for="pickerPropsFor"
        @item-click="onItemClick"
        @item-count-change="changeCount"
        @copy-to-file-browser="(item) => $emit('copy-to-file-browser', item)"
        @open-preview="openPreview"
      />
    </div>

    <div
      v-else
      class="external-content-view__sections"
    >
      <div
        v-if="folderItems.length"
        class="external-content-view__section"
      >
        <div
          v-if="fileItems.length"
          class="external-content-view__section-title"
        >
          Folders
        </div>
        <ItemGrid
          class="folder-items"
          :items="folderItems"
          :is-selected="isSelected"
          :columns="gridColumns"
          :columns-md="gridColumnsMd"
          :item-props="{ isAvailableOverlay: false, showCheckbox: false }"
          @item-click="onItemClick"
        />
      </div>

      <div
        v-if="fileItems.length"
        class="external-content-view__section"
      >
        <div
          v-if="folderItems.length"
          class="external-content-view__section-title"
        >
          Files
        </div>
        <ItemGrid
          :items="fileItems"
          :is-selected="isSelected"
          :columns="gridColumns"
          :columns-md="gridColumnsMd"
          :item-props="{ isAvailableOverlay: false, showCheckbox: true }"
          :item-props-for="counterPropsFor"
          @item-click="onItemClick"
          @item-count-change="changeCount"
          @copy-to-file-browser="(item) => $emit('copy-to-file-browser', item)"
          @open-preview="openPreview"
        />
      </div>
    </div>

    <ItemPagination
      v-if="lastPage > 1"
      v-model="currentPage"
      :label-only="labelPager"
      :total-rows="lastPage * pageSize"
      :per-page="pageSize"
      @change="fetchItems"
    />

    <AssetPreview
      ref="assetPreview"
      :item="previewItem"
      @confirm-selection="onConfirmSelection"
    />
  </div>
</template>

<script>
import ItemPagination from "@frontend/components/modules/file-browser/browser/ItemPagination.vue";
import ItemGrid from '@frontend/components/modules/file-browser/browser/ItemGrid.vue';
import AssetPreview from '@frontend/components/modules/file-browser/modals/AssetPreview.vue';
import SelectFolderModalToolbar from '@frontend/components/modules/file-browser/modals/SelectFolderModalToolbar.vue';
import { GRID_COLUMNS, GRID_COLUMNS_MD, GRID_PAGE_SIZE } from '@frontend/constants/select-image-grid';
import { isPreviewableImage } from '@frontend/components/modules/file-browser/helpers.js';

export default {
  components: {
    ItemPagination,
    ItemGrid,
    AssetPreview,
    SelectFolderModalToolbar
  },
  props: {
    endpoint: {
      type: String,
      required: true,
    },
    pageSize: {
      type: Number,
      default: GRID_PAGE_SIZE,
    },
    extraParams: {
      type: Object,
      default: () => ({}),
    },
    showSearch: {
      type: Boolean,
      default: true,
    },
    // The legacy modal has its own full-size viewer that seven editing tools hook into.
    externalPreview: {
      type: Boolean,
      default: false,
    },
    showCopyToFileBrowser: {
      type: Boolean,
      default: false,
    },
    navigableFolders: {
      type: Boolean,
      default: false,
    },
    columns: {
      type: Number,
      default: null,
    },
    columnsMd: {
      type: Number,
      default: null,
    },
    fitRows: {
      type: Boolean,
      default: false,
    },
    bandToolbar: {
      type: Boolean,
      default: false,
    },
    labelPager: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      items: [],
      folderId: 0,
      searchKey: '',
      selectedIds: [],
      selectedItems: [],
      currentPage: 1,
      totalRows: 0,
      lastPage: 0,
      isLoading: false,
      searchTimer: null,
      previewItem: null,
      allowCounter: false,
      fetchSeq: 0,
      counts: {},
    };
  },
  computed: {
    gridColumns() {
      return this.columns || GRID_COLUMNS;
    },
    gridColumnsMd() {
      return this.columnsMd || GRID_COLUMNS_MD;
    },
    folderItems() {
      return this.items.filter((item) => item.type === 'folder');
    },
    fileItems() {
      return this.items.filter((item) => item.type !== 'folder');
    },
  },
  watch: {
    endpoint() {
      this.reset();
    },
    // The host re-pushes the same params on every tab activation, so refetch only on a real change.
    extraParams(next, prev) {
      if (JSON.stringify(next) === JSON.stringify(prev)) {
        return;
      }

      this.fetchItems(1);
    },
  },
  created() {
    this.fetchItems(1);
  },
  methods: {
    async fetchItems(page = 1) {
      // Source switches and toolbar pushes can leave two fetches in flight; without this the
      // slower one wins and renders the previous source's payload.
      const requestId = (this.fetchSeq += 1);

      this.isLoading = true;

      try {
        const { data } = await axios.get(this.endpoint, {
          params: {
            pageNumber: page,
            pageSize: this.pageSize,
            searchKey: this.searchKey,
            ...(this.navigableFolders ? { folder_id: this.folderId } : {}),
            ...this.extraParams,
          },
        });

        if (requestId !== this.fetchSeq) {
          return;
        }

        if (data?.status === 'error') {
          toastr.error(data.message || 'Failed to load items');
          return;
        }

        this.items = data.items || [];
        this.currentPage = data.current_page || page;
        this.totalRows = data.totalCount || 0;
        this.lastPage = data.last_page || 0;
      } catch (error) {
        console.error('Error fetching items:', error);

        if (requestId === this.fetchSeq) {
          toastr.error('Failed to load items');
        }
      } finally {
        if (requestId === this.fetchSeq) {
          this.isLoading = false;
        }
      }
    },

    isSelected(item) {
      return this.selectedIds.includes(item.id);
    },

    onItemClick(item) {
      if (this.navigableFolders && item.type === 'folder') {
        this.folderId = item.id;
        this.searchKey = '';
        this.currentPage = 1;
        this.clearSelection();
        this.fetchItems(1);

        return;
      }

      this.toggleSelect(item);
    },

    pickerPropsFor(item) {
      if (item.type === 'folder') {
        return { showCheckbox: false };
      }

      return {
        showCheckbox: true,
        ...this.counterPropsFor(item),
      };
    },

    counterPropsFor(item) {
      return {
        showMagnifier: isPreviewableImage(item),
        showCounter: this.allowCounter && this.isSelected(item),
        count: this.counts[item.id] || 1,
        showCopyToFileBrowser: this.showCopyToFileBrowser,
      };
    },

    setMultiple(value) {
      this.allowCounter = Boolean(value);

      if (!this.allowCounter) {
        this.counts = {};
      }
    },

    changeCount({ item, delta }) {
      const next = (this.counts[item.id] || 1) + delta;

      if (next <= 0) {
        this.$delete(this.counts, item.id);
        this.toggleSelect(item);

        return;
      }

      this.$set(this.counts, item.id, next);
      this.emitSelection();
    },

    emitSelection() {
      this.$emit('onSelectedItems', this.selectedItems.map((item) => ({
        ...item,
        count: this.counts[item.id] || 1,
      })));
    },

    clearSelection() {
      this.selectedIds = [];
      this.selectedItems = [];
      // Or re-selecting the same image inherits its old multi-placement count.
      this.counts = {};
      this.$emit('onSelectedItems', []);
    },

    toggleSelect(item) {
      const idx = this.selectedIds.indexOf(item.id);

      if (idx === -1) {
        this.selectedIds = [...this.selectedIds, item.id];
        this.selectedItems = [...this.selectedItems, item];
      } else {
        this.selectedIds = this.selectedIds.filter((id) => id !== item.id);
        this.selectedItems = this.selectedItems.filter((i) => i.id !== item.id);
        this.$delete(this.counts, item.id);
      }

      this.emitSelection();
    },

    onSearchInput(value) {
      this.searchKey = value;
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.fetchItems(1), 400);
    },

    onSearchSubmit() {
      clearTimeout(this.searchTimer);
      this.fetchItems(1);
    },

    clearSearch() {
      this.searchKey = '';
      this.onSearchSubmit();
    },

    onConfirmSelection(assets) {
      assets.forEach((asset) => this.toggleSelect(asset));
      this.$emit('autoConfirm');
    },

    openPreview(item) {
      if (this.externalPreview) {
        this.$emit('open-preview', item);

        return;
      }

      this.previewItem = item;
      this.$refs.assetPreview.show();
    },

    reset() {
      this.items = [];
      this.folderId = 0;
      this.searchKey = '';
      this.selectedIds = [];
      this.selectedItems = [];
      this.counts = {};
      this.currentPage = 1;
      this.totalRows = 0;
      this.lastPage = 0;
      this.fetchItems(1);
    },
  },
};
</script>

<style scoped>
.external-content-view {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.external-content-view__state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  color: #73808c;
}

.external-content-view__sections {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.external-content-view__section + .external-content-view__section {
  padding-top: 16px;
  border-top: 1px solid #e6e9ec;
}

.external-content-view__section-title {
  margin-bottom: 8px;
  font-family: "Nunito", sans-serif;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #8a929a;
}
</style>
